import React from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import {
    Config,
    jsonp,
    I18n,
    I18nContext,
    getQueryParams,
    shuffleArray,
    getCookie,
    DeviceType,
} from '@deporvillage/utils-library-component';
import { initialize as initializeTracking, track } from '@deporvillage/tracking-library-component';
import ReviewPopup, { ReviewPopupType } from '@deporvillage/review-popup-component';
import TopBrands, { TopBrandsClick } from '@deporvillage/top-brands-component';
import Swiper, { SwiperArrowType } from '@deporvillage/swiper-component';
import ScrollToTopButton from '@deporvillage/scroll-to-top-button-component';
import ProductSlider from '@deporvillage/product-slider-component';
import AdManager, { AdManagerId, init as InitAdManager } from '@deporvillage/ad-manager-component';
import ReviewsTrustpilot from '@deporvillage/reviews-trustpilot-component';
import Picture from '@deporvillage/picture-component';
import { LoginPopupExtended } from '@deporvillage/login-popup-component';
import { trackEeViewItemList } from '@deporvillage/frontend-common';
import { RestorePasswordPopupExtended } from '@deporvillage/restore-password-popup-component';
import ForgotPasswordPopup from '@deporvillage/forgot-password-popup-component';
import Mosaic from './Mosaic';
import PhotoSlurp, { PhotoSlurpClick } from './PhotoSlurp';

import styles from './Home.module.scss';

const Cookies = {
    Tracking: 'dpvUid',
};

const ProductSliderBlockLimit = 8;

const RecommendedProductBlock = {
    RecentViews: {
        type: 'recent-views',
        fallback: 'top-views-products',
    },
    VvLastCart: {
        type: 'vv-last-cart',
        fallback: 'top-sales-products',
    },
};

const ProductSliderBreakpoints = {
    0: {
        slidesPerView: 2.5,
        slidesPerGroup: 1,
        spaceBetween: 0,
        freeMode: true,
    },
    575: {
        slidesPerView: 3.5,
        slidesPerGroup: 1,
        spaceBetween: 0,
        freeMode: true,
    },
    1034: {
        slidesPerView: 4,
        slidesPerGroup: 4,
        spaceBetween: 0,
        freeMode: false,
    },
};

const SliderTopBrandsBreakpoints = {
    0: {
        slidesPerView: 'auto',
        freeMode: true,
    },
    768: {
        slidesPerView: 8,
        freeMode: true,
    },
    1035: {
        slidesPerView: 11,
        freeMode: true,
    },
    1415: {
        slidesPerView: 14,
        freeMode: true,
    },
};

const sfmcProductMapper = (res, currencyCode, conversion) => ({
    title: res.title,
    name: res.name,
    items: res.items.slice(0, 8).reduce((previous, current) => [
        ...previous, {
            title: current.name,
            gallery: {
                default: 0,
                images: [current.image_link.split('/').pop()],
            },
            link: current.link,
            discountTag: {
                show: +current.show_discount &&
                    (current.pvp_float - current.correct_price_float) / current.pvp_float >= 0.2,
            },
            defaultVariant: {
                price: {
                    pvp: current.pvp_float * conversion,
                    price: current.correct_price_float * conversion,
                    currency: currencyCode,
                },
            },
        },
    ], []),
});

const fetchSfmc = async (id, locale, currency, conversion) => {
    const ans = await jsonp(`//100006977.recs.igodigital.com/a/v2/100006977/${id}/recommend.json?locale=${locale.toLowerCase().replace('_', '-')}`);
    const res = await ans.json();
    // istanbul ignore if
    if (res?.error || !res?.[0]?.items) return null;
    return sfmcProductMapper(res[0], currency, conversion);
};

// istanbul ignore next
const getRecommendedProductBlock = ({
    api, block, lang, country, id,
}) =>
    fetch(`${api}/api/recommendation/${id}/${block.type}/${lang}/${country}?${Date.now()}`, { priority: 'low' })
        .then((ans) => ans.json())
        .then((ans) => ({
            visible: !!ans?.items?.length,
            items: ans?.items.filter((i) => i).slice(0, ProductSliderBlockLimit) || null,
        }))
        .catch(() => ({ visible: false, items: null }));

const getFallbackProductBlock = ({
    api, block, lang, country,
}) =>
    fetch(`${api}/api/recommendation/product/${block.fallback}/${lang}/${country}`, { priority: 'low' })
        .then((ans) => ans.json())
        .then((ans) => ({
            visible: !!ans?.items?.length,
            items:
                /* istanbul ignore next */
                ans?.items.filter((i) => i).slice(0, ProductSliderBlockLimit) || null,
        }))
        .catch(
            /* istanbul ignore next */
            () => ({ visible: false, items: null }),
        );

const fetchProductBlock = ({
    api, block, lang, country,
}) => {
    const id = getCookie(Cookies.Tracking);
    // istanbul ignore if
    if (id) {
        return getRecommendedProductBlock({
            api, block, lang, country, id,
        });
    }
    return getFallbackProductBlock({
        api, block, lang, country,
    });
};

const mapForDataLayerTracking = (items) => items?.map(({
    title,
    sku,
    isNew,
    defaultVariant,
}) => ({
    title,
    sku,
    isNew,
    price: defaultVariant?.price?.price,
}));

const redirect = (ev) => (ev.data.type === 'redirect') && (window.location.assign(ev.data.url));

const HomePage = ({ model }) => {
    const { translate } = React.useContext(I18nContext);
    const {
        tracking, debug, ad, lang, api, store, country,
        isMobile, width, trustpilot, canRunAds,
        isOptanonInited, device, locale, isEeTrackingEnabled,
    } = React.useContext(Config);

    const [sfmc3, setSfmc3] = React.useState(null);
    const [recentViews, setRecentViews] = React.useState({ visible: true, items: null });
    const [vvLastCart, setVvLastCart] = React.useState({ visible: true, items: null });
    const [reviewUuids, setReviewUuids] = React.useState({});
    const [showLogin, setShowLogin] = React.useState(false);
    const [showRestorePassword, setShowRestorePassword] = React.useState(false);
    const [showForgotPassword, setShowForgotPassword] = React.useState(false);
    const [restorePasswordToken, setRestorePasswordToken] = React.useState(null);

    const handleOnForgotPasswordClose = () => {
        setShowForgotPassword(!showForgotPassword);
        window.location.href = '/';
    };

    const adHomeSliderBanners = React.useMemo(() => {
        if (!ad?.homeTopSlider) return [];
        if (!ad?.config?.isHomeSliderFixed) return shuffleArray(ad?.homeTopSlider);
        return [ad?.homeTopSlider?.[0], ...shuffleArray(ad?.homeTopSlider?.slice(1))];
    }, [ad]);

    const conversion = React.useMemo(() => (
        store.currency_conversion_factor / country.currency_conversion_factor
    ), [store, country]);

    const getProductBlockSfmc = async () => {
        const res = await fetchSfmc(
            'home3',
            locale,
            country.currency_code,
            conversion,
        );
        setSfmc3(res);
        isEeTrackingEnabled && trackEeViewItemList({
            listName: 'product-slider-sf_home',
            listId: 'product-slider-sf',
            items: mapForDataLayerTracking(res?.items),
        });
    };

    const getProductBlockRecentViews = async () => {
        const res = await fetchProductBlock({
            api,
            block: RecommendedProductBlock.RecentViews,
            lang,
            country: country.code,
        });
        setRecentViews(res);
        isEeTrackingEnabled && trackEeViewItemList({
            listName: 'product-slider-rv_home',
            listId: 'product-slider-rv',
            items: mapForDataLayerTracking(res?.items),
        });
    };

    const getProductBlockLastCart = async () => {
        const res = await fetchProductBlock({
            api,
            block: RecommendedProductBlock.VvLastCart,
            lang,
            country: country.code,
        });
        setVvLastCart(res);
        isEeTrackingEnabled && trackEeViewItemList({
            listName: 'product-slider-vvlc_home',
            listId: 'product-slider-vvlc',
            items: mapForDataLayerTracking(res?.items),
        });
    };

    const handleTopBrandsClick = (ev) => {
        switch (ev) {
            case TopBrandsClick.ShowMore:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Marcas Slide',
                    label: 'Click en boton Más marcas',
                });
                break;
            case TopBrandsClick.BrandItem:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a Marcas Slide',
                    label: 'Click en logo marca',
                });
                break;
        }
    };

    const handlePhotoSlurpClick = (type) => {
        switch (type) {
            case PhotoSlurpClick.Gallery:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Posts Instagram',
                    label: 'Click en boton Ver galeria',
                });
                break;
            case PhotoSlurpClick.GalleryNext:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Posts Instagram',
                    label: 'Click en Ver siguiente',
                });
                break;
            case PhotoSlurpClick.GalleryPrev:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Posts Instagram',
                    label: 'Click en Ver anterior',
                });
                break;
            case PhotoSlurpClick.Picture:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Posts Instagram',
                    label: 'Click en imagen post',
                });
                break;
            case PhotoSlurpClick.Tag:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver Posts Instagram',
                    label: 'Click en Hashtag',
                });
                break;
        }
    };

    const handleOnClickMosaic = (position) => track({
        type: 'ACTION',
        name: 'GENERIC_CLICK',
        action: 'Ir a Categorías Mosaic',
        label: `Click en Categoria Mosaic P${position}`,
    });

    const handleClickAdsSwiper = (ev, idx) => {
        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Ir a Slide-header',
            label: `Click en imagen slide-header ${idx}`,
        });

        // istanbul ignore if
        if (ev.target.querySelector('iframe')) {
            ev.target.querySelector('iframe').contentWindow.postMessage({ type: 'click' }, '*');
        }
    };

    const handleUserData = ({ detail }) => {
        if (detail?.logged_in) return;
        const {
            login, restore_password: restorePasword, forgot_password: forgotPassword, token,
        } = getQueryParams(window?.location?.href);
        setShowLogin(!!login);
        setRestorePasswordToken(token);
        setShowRestorePassword(!!restorePasword);
        setShowForgotPassword(!!forgotPassword);
    };

    React.useEffect(() => {
        if (!canRunAds || !ad) return;
        InitAdManager([
            { ...adHomeSliderBanners?.[0], isPriorityBanner: true },
            { ...ad?.promo, isPriorityBanner: true },
            adHomeSliderBanners?.[1],
            adHomeSliderBanners?.[2],
            adHomeSliderBanners?.[3],
            ad?.homeMiddle1x1,
            ad?.homeMiddle2x1,
            ad?.homeMiddle2x2,
            ad?.homeMiddle3x1,
            ad?.homeMiddle3x2,
            ad?.homeMiddle3x3,
            isMobile ? ad?.footerMobile : ad?.footer,
        ], lang);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [canRunAds, ad]);

    React.useEffect(() => {
        window.addEventListener('message', redirect);
        return () => {
            window.removeEventListener('message', redirect);
        };
    }, []);

    React.useEffect(() => {
        const {
            opt, skey, rtm,
            product_review: productReview,
            service_review: serviceReview,
            first: initOrder,
        } = getQueryParams(window.location.href);

        initializeTracking({
            ...tracking,
            page: 'home',
            debug,
        });

        track({
            type: 'SET',
            name: 'setPage',
            data: {
                page: 'home',
                opt,
                skey,
                rtm,
            },
        });

        track({
            type: 'ACTION',
            name: 'VISIT',
        });

        getProductBlockSfmc();
        getProductBlockLastCart();
        getProductBlockRecentViews();

        setReviewUuids({
            product: productReview,
            service: serviceReview,
            initOrder,
        });

        window.addEventListener('user:data', handleUserData);
        return () => {
            window.removeEventListener('user:data', handleUserData);
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    return (
        <div className={styles['home-component']}>

            <div className={styles['ads-home-top']}>
                <div className={styles['ads-promo-wrapper']}>
                    {
                        canRunAds ? (
                            <AdManager
                                id={AdManagerId.Promo}
                                divId={ad?.promo.divId}
                            />
                        ) : (
                            !ad?.promo?.defaultImage ? null : (
                                isOptanonInited && (
                                    <a href={ad?.promo.defaultUrl || null}>
                                        <Picture
                                            sizes={{
                                                360: '360x38',
                                                500: '500x53',
                                                767: '767x84',
                                                1034: '750x38',
                                                1399: '990x50',
                                            }}
                                            width={1730}
                                            height={50}
                                            src={`static/banners/adblock/promo/${ad?.promo.defaultImage[device]}`}
                                        />
                                    </a>
                                )
                            )
                        )
                    }
                </div>

                {
                    !canRunAds ? (
                        !ad?.homeTopSlider?.[0]?.defaultImage ? null : (
                            <a className={styles['ads-home-slider-fixed']} href={ad?.homeTopSlider[0].defaultUrl || null}>
                                {
                                    isOptanonInited ? (
                                        <Picture
                                            sizes={{
                                                360: '360x238',
                                                500: '500x331',
                                                767: '767x508',
                                                1034: '750x497',
                                                1399: '990x376',
                                            }}
                                            width={1370}
                                            height={520}
                                            src={`static/banners/home/${ad?.homeTopSlider[0].defaultImage[isMobile ? DeviceType.Mobile : DeviceType.Desktop]}`}
                                            priority={'high'}
                                        />
                                    ) : null
                                }
                            </a>
                        )
                    ) : (
                        <div className={styles['ads-home-slider']}>
                            {
                                <Swiper
                                    arrows={isMobile ? SwiperArrowType.None : SwiperArrowType.In}
                                    loop
                                    pagination
                                    autoplay
                                    disableOnInteraction
                                >
                                    {
                                        adHomeSliderBanners.map((b, idx) => (
                                            <div className={styles['ad-manager-wrapper']} key={`home-slider-${b.divId}`}>
                                                <AdManager
                                                    id={b.id}
                                                    divId={b.divId}
                                                    onClick={
                                                        (ev) => handleClickAdsSwiper(ev, idx + 1)
                                                    }
                                                />
                                            </div>
                                        ))
                                    }
                                </Swiper>
                            }
                        </div>
                    )
                }

            </div>

            <div className={styles['section-3']}>
                <TopBrands
                    items={model.topBrands}
                    url={translate('home_page.url.more_brands')}
                    breakpoints={SliderTopBrandsBreakpoints}
                    onClick={handleTopBrandsClick}
                />
            </div>

            {
                model.mosaic && (
                    <div className={`${styles['section-3']} ${styles['home-page-mosaic-wrapper']}`}>
                        <Mosaic
                            items={model.mosaic}
                            onClick={handleOnClickMosaic}
                        />
                    </div>
                )
            }

            {
                recentViews?.visible && (
                    <div
                        id={'product-slider-rv'}
                        className={`${styles['section-5']} ${styles['home-product-slider']}`}
                    >
                        <LazyLoad>
                            {
                                recentViews?.items && (
                                    <ProductSlider
                                        breakpoints={ProductSliderBreakpoints}
                                        title={translate('home_page.title.recommended_products')}
                                        onClick={() =>
                                            track({
                                                type: 'ACTION',
                                                name: 'GENERIC_CLICK',
                                                action: 'Ir a Producto en Slide 1',
                                                label: 'Click en Producto de Slide',
                                            })
                                        }
                                        anchor
                                        items={recentViews.items}
                                    />
                                )
                            }
                        </LazyLoad>
                    </div>
                )
            }

            {
                vvLastCart?.visible && (
                    <div
                        id={'product-slider-vvlc'}
                        className={`${styles['section-5']} ${styles['home-product-slider']}`}
                    >
                        <LazyLoad>
                            {
                                vvLastCart?.items && (
                                    <ProductSlider
                                        breakpoints={ProductSliderBreakpoints}
                                        title={translate('home_page.title.viewed_products')}
                                        onClick={() =>
                                            track({
                                                type: 'ACTION',
                                                name: 'GENERIC_CLICK',
                                                action: 'Ir a Producto en Slide 2',
                                                label: 'Click en Producto de Slide',
                                            })
                                        }
                                        anchor
                                        items={vvLastCart.items}
                                    />
                                )
                            }
                        </LazyLoad>
                    </div>
                )
            }

            <div>
                {
                    !canRunAds ? (
                        <>
                            <h2>
                                <I18n>home_page.title.ad_offers</I18n>
                            </h2>

                            <div className={`${styles['ads-home-mid']} ${styles['ads-home-mid-2x']}`}>

                                {
                                    !isOptanonInited ||
                                        !ad?.homeMiddle2x1.defaultImage ? null : (
                                        <a href={
                                            ad?.homeMiddle2x1.defaultUrl || null
                                        }>
                                            <Picture
                                                width={width > 1414 ? 675 : (width < 768 ? '100%' : 368)}
                                                lazy
                                                src={`static/banners/home/2x/${ad?.homeMiddle2x1.defaultImage[
                                                    width < 768 ?
                                                        DeviceType.Mobile : DeviceType.Desktop
                                                ]}`}
                                            />
                                        </a>
                                    )
                                }
                                {
                                    !isOptanonInited ||
                                        !ad?.homeMiddle2x2.defaultImage ? null : (
                                        <a href={
                                            ad?.homeMiddle2x2.defaultUrl || null
                                        }>
                                            <Picture
                                                width={width > 1414 ? 675 : (width < 768 ? '100%' : 675)}
                                                lazy
                                                src={`static/banners/home/2x/${ad?.homeMiddle2x2.defaultImage[
                                                    width < 768 ?
                                                        DeviceType.Mobile : DeviceType.Desktop
                                                ]}`}
                                            />
                                        </a>
                                    )
                                }
                            </div>

                            <h2>
                                <I18n>home_page.title.ad_new</I18n>
                            </h2>

                            <div className={`${styles['ads-home-mid']} ${styles['ads-home-mid-1x']}`}>
                                {
                                    !isOptanonInited ||
                                        !ad?.homeMiddle1x1.defaultImage ? null : (
                                        <a href={
                                            ad?.homeMiddle1x1.defaultUrl || null
                                        }>
                                            <Picture
                                                width={width > 1414 ? 1370 : (width < 768 ? '100%' : 750)}
                                                lazy
                                                src={`static/banners/home/1x/${ad?.homeMiddle1x1.defaultImage[
                                                    width < 768 ?
                                                        DeviceType.Mobile : DeviceType.Desktop
                                                ]}`}
                                            />
                                        </a>
                                    )
                                }
                            </div>

                            <div className={`${styles['ads-home-mid']} ${styles['ads-home-mid-3x']}`}>
                                {
                                    !isOptanonInited ||
                                        !ad?.homeMiddle3x1.defaultImage ? null : (
                                        <a href={
                                            ad?.homeMiddle3x1.defaultUrl || null
                                        }>
                                            <Picture
                                                width={width > 1414 ? 445 : (width < 768 ? '100%' : 236)}
                                                lazy
                                                src={`static/banners/home/3x/${ad?.homeMiddle3x1.defaultImage[
                                                    width < 768 ?
                                                        DeviceType.Mobile : DeviceType.Desktop
                                                ]}`}
                                            />
                                        </a>
                                    )
                                }
                                {
                                    !isOptanonInited ||
                                        !ad?.homeMiddle3x2.defaultImage ? null : (
                                        <a href={
                                            ad?.homeMiddle3x2.defaultUrl || null
                                        }>
                                            <Picture
                                                width={width > 1414 ? 445 : (width < 768 ? '100%' : 236)}
                                                lazy
                                                src={`static/banners/home/3x/${ad?.homeMiddle3x2.defaultImage[
                                                    width < 768 ?
                                                        DeviceType.Mobile : DeviceType.Desktop
                                                ]}`}
                                            />
                                        </a>
                                    )
                                }
                                {
                                    !isOptanonInited ||
                                        !ad?.homeMiddle3x3.defaultImage ? null : (
                                        <a href={
                                            ad?.homeMiddle3x3.defaultUrl || null
                                        }>
                                            <Picture
                                                width={width > 1414 ? 445 : (width < 768 ? '100%' : 236)}
                                                lazy
                                                src={`static/banners/home/3x/${ad?.homeMiddle3x3.defaultImage[
                                                    width < 768 ?
                                                        DeviceType.Mobile : DeviceType.Desktop
                                                ]}`}
                                            />
                                        </a>
                                    )
                                }
                            </div>
                        </>
                    ) : (
                        <>
                            <h2>
                                <I18n>home_page.title.ad_offers</I18n>
                            </h2>

                            <div className={`${styles['ads-home-mid']} ${styles['ads-home-mid-2x']}`}>
                                <AdManager
                                    id={AdManagerId.HomeMid21}
                                    divId={ad?.homeMiddle2x1.divId}
                                />
                                <AdManager
                                    id={AdManagerId.HomeMid22}
                                    divId={ad?.homeMiddle2x2.divId}
                                />
                            </div>

                            <h2>
                                <I18n>home_page.title.ad_new</I18n>
                            </h2>

                            <div className={`${styles['ads-home-mid']} ${styles['ads-home-mid-1x']}`}>
                                <AdManager
                                    id={AdManagerId.HomeMid1}
                                    divId={ad?.homeMiddle1x1.divId}
                                />
                            </div>

                            <div className={`${styles['ads-home-mid']} ${styles['ads-home-mid-3x']}`}>
                                <AdManager
                                    id={AdManagerId.HomeMid31}
                                    divId={ad?.homeMiddle3x1.divId}
                                />
                                <AdManager
                                    id={AdManagerId.HomeMid32}
                                    divId={ad?.homeMiddle3x2.divId}
                                />
                                <AdManager
                                    id={AdManagerId.HomeMid33}
                                    divId={ad?.homeMiddle3x3.divId}
                                />
                            </div>
                        </>
                    )
                }
            </div>

            <div
                id={'product-slider-sf'}
                className={`${styles['section-5']} ${styles['home-product-slider']}`}
            >
                <LazyLoad>
                    {
                        sfmc3?.items && (
                            <ProductSlider
                                breakpoints={ProductSliderBreakpoints}
                                title={translate('home_page.title.favorite_products')}
                                onClick={() =>
                                    track({
                                        type: 'ACTION',
                                        name: 'GENERIC_CLICK',
                                        action: 'Ir a Producto en Slide 3',
                                        label: 'Click en Producto de Slide',
                                    })
                                }
                                anchor
                                items={sfmc3.items}
                            />
                        )
                    }
                </LazyLoad>
            </div>

            {
                trustpilot?.enabled?.reviews && (
                    <div className={styles['section-5']}>
                        <h2>
                            <I18n>home_page.reviews.title</I18n>
                        </h2>
                        <div className={'reviews-trustpilot-wrapper'}>
                            <LazyLoad>
                                <ReviewsTrustpilot />
                            </LazyLoad>
                        </div>
                    </div>
                )
            }

            <div className={styles['section-5']}>
                <LazyLoad>
                    <PhotoSlurp onClick={handlePhotoSlurpClick} />
                </LazyLoad>
            </div>

            {
                canRunAds ? (
                    <div className={styles['ads-footer-wrapper']}>
                        <AdManager
                            id={isMobile ? AdManagerId.FooterMobile : AdManagerId.Footer}
                            divId={ad?.[`footer${isMobile ? 'Mobile' : ''}`].divId}
                        />
                    </div>
                ) : (
                    !isOptanonInited || !ad?.footer?.defaultImage ? null : (
                        <div className={styles['ads-footer-wrapper']}>
                            <Picture
                                width={isMobile ? 320 : 728}
                                height={isMobile ? 50 : 90}
                                src={
                                    `static/banners/adblock/footer/${ad?.footer?.defaultImage[
                                    isMobile ? DeviceType.Mobile : DeviceType.Desktop
                                    ]
                                    }`
                                }
                                lazy
                            />
                        </div>
                    )
                )
            }

            {
                (reviewUuids?.service || reviewUuids?.product) && (
                    <ReviewPopup
                        data={{
                            uuidProduct: reviewUuids?.product,
                            uuidService: reviewUuids?.service,
                            initOrder: reviewUuids?.initOrder,
                        }}
                        type={ReviewPopupType.ProductService}
                        onClose={() => setReviewUuids({})}
                    />
                )
            }
            {

                showLogin && (
                    <LoginPopupExtended redirect={'/customer/account'} onClose={() => (window.location.href = '/')} />
                )
            }
            {

                showRestorePassword && (
                    <RestorePasswordPopupExtended redirect={'/'} onClose={() => (window.location.href = '/')} token={restorePasswordToken} />
                )
            }

            <ForgotPasswordPopup isOpen={showForgotPassword}
            onClose={handleOnForgotPasswordClose} />

            <div className={styles['scroll-to-top-wrapper']}>
                <ScrollToTopButton breakpoint={1000} />
            </div>
        </div>
    );
};

HomePage.propTypes = {
    model: PropTypes.object,
    config: PropTypes.object,
};

export default HomePage;
